// ws-product-hero.js — Latency Studio
// 1. Hover/focus/tap a swatch → swap the product hero <img> to that swatch's hero image.
// 2. Optional auto-rotate: cycle through swatches that have a hero image at a merchant-
//    configured interval. Click behaviour (lock vs. pause), interval, and pause-on-hover
//    are all read from data-* on the section root, so everything is editor-driven.
//
// All swaps are instant (no cross-fade). Idempotent against double-loading and against
// theme-editor section reloads.

(function () {
  if (window.__wsProductHeroBound) return;
  window.__wsProductHeroBound = true;

  function getHero(swatch) {
    const root = swatch.closest('[data-ws-product-hero]');
    if (!root) return null;
    const target = root.querySelector('[data-ws-hero-target]');
    if (!target) return null;
    return target.querySelector('img');
  }

  function getColorNameEl(root) {
    return root ? root.querySelector('[data-ws-hero-color-name]') : null;
  }

  function setColorName(root, name) {
    const el = getColorNameEl(root);
    if (!el) return;
    if (name) {
      el.textContent = name;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function cacheDefault(img) {
    if (img.dataset.wsHeroDefaultSrc) return;
    img.dataset.wsHeroDefaultSrc = img.getAttribute('src') || '';
    img.dataset.wsHeroDefaultSrcset = img.getAttribute('srcset') || '';
    img.dataset.wsHeroDefaultAlt = img.getAttribute('alt') || '';
  }

  function setActive(swatch) {
    const root = swatch.closest('[data-ws-product-hero]');
    if (!root) return;
    root.querySelectorAll('[data-ws-swatch]').forEach(function (s) {
      s.removeAttribute('data-ws-active');
    });
    swatch.setAttribute('data-ws-active', 'true');
  }

  function applySwatch(swatch) {
    const root = swatch.closest('[data-ws-product-hero]');
    const src = swatch.getAttribute('data-ws-hero-src');
    const colorName = swatch.getAttribute('data-ws-color-name') || '';

    if (src) {
      const img = getHero(swatch);
      if (img) {
        cacheDefault(img);
        img.setAttribute('src', src);
        const srcset = swatch.getAttribute('data-ws-hero-srcset') || '';
        if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.removeAttribute('srcset');
        }
        const alt = swatch.getAttribute('data-ws-hero-alt');
        if (alt) img.setAttribute('alt', alt);
      }
    }

    if (root) setColorName(root, colorName);
    setActive(swatch);
  }

  function restoreDefault(root) {
    const target = root.querySelector('[data-ws-hero-target]');
    if (target) {
      const img = target.querySelector('img');
      if (img && img.dataset.wsHeroDefaultSrc) {
        img.setAttribute('src', img.dataset.wsHeroDefaultSrc);
        if (img.dataset.wsHeroDefaultSrcset) {
          img.setAttribute('srcset', img.dataset.wsHeroDefaultSrcset);
        } else {
          img.removeAttribute('srcset');
        }
        if (img.dataset.wsHeroDefaultAlt) img.setAttribute('alt', img.dataset.wsHeroDefaultAlt);
      }
    }
    setColorName(root, '');
  }

  // Hover / focus → swap. Mouse leave the whole hero → restore default (unless auto-rotate
  // is running — then we let the rotator stay in control).
  document.addEventListener(
    'mouseover',
    function (e) {
      const swatch = e.target.closest('[data-ws-swatch]');
      if (!swatch) return;
      applySwatch(swatch);
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (e) {
      const swatch = e.target.closest('[data-ws-swatch]');
      if (!swatch) return;
      applySwatch(swatch);
    },
    true
  );

  document.addEventListener('click', function (e) {
    const swatch = e.target.closest('[data-ws-swatch]');
    if (!swatch) return;
    if (!swatch.getAttribute('data-ws-hero-src') && !swatch.getAttribute('data-ws-color-name')) return;
    const link = e.target.closest('a[href]');
    if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') return;
    applySwatch(swatch);
  });

  document.addEventListener(
    'mouseleave',
    function (e) {
      const root = e.target.closest && e.target.closest('[data-ws-product-hero]');
      if (!root) return;
      // Auto-rotate manages its own state — don't yank the image back to default.
      if (root.dataset.wsAutoRotate === 'true') return;
      restoreDefault(root);
    },
    true
  );

  // ---------- Auto-rotate ----------

  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initAutoRotate(root) {
    if (root.__wsAutoRotateInit) return;
    root.__wsAutoRotateInit = true;

    if (root.dataset.wsAutoRotate !== 'true') return;
    if (reducedMotion) return;

    const swatches = Array.from(
      root.querySelectorAll('[data-ws-swatch][data-ws-hero-src]')
    );
    if (swatches.length < 2) return;

    const interval = parseInt(root.dataset.wsAutoRotateInterval, 10) || 3000;
    const clickBehavior = root.dataset.wsAutoRotateClick || 'lock';
    const pauseOnHover = root.dataset.wsAutoRotatePauseOnHover !== 'false';

    let index = 0;
    let timer = null;
    let locked = false;
    let hoverPaused = false;
    let offscreenPaused = true; // start paused; IntersectionObserver flips it

    function tick() {
      index = (index + 1) % swatches.length;
      applySwatch(swatches[index]);
    }

    function start() {
      if (locked || hoverPaused || offscreenPaused) return;
      if (timer) return;
      timer = setInterval(tick, interval);
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    // Sync visual state to swatch index 0 so the active ring matches the first frame.
    applySwatch(swatches[0]);

    if (pauseOnHover) {
      root.addEventListener('mouseenter', function () {
        hoverPaused = true;
        stop();
      });
      root.addEventListener('mouseleave', function () {
        hoverPaused = false;
        start();
      });
      root.addEventListener('focusin', function () {
        hoverPaused = true;
        stop();
      });
      root.addEventListener('focusout', function (e) {
        if (root.contains(e.relatedTarget)) return;
        hoverPaused = false;
        start();
      });
    }

    root.addEventListener('click', function (e) {
      const swatch = e.target.closest('[data-ws-swatch]');
      if (!swatch || !root.contains(swatch)) return;
      if (!swatch.getAttribute('data-ws-hero-src')) return;

      const clickedIndex = swatches.indexOf(swatch);
      if (clickedIndex >= 0) index = clickedIndex;

      if (clickBehavior === 'lock') {
        locked = true;
        stop();
      }
      // 'pause' → rotation stays alive; hoverPaused/offscreenPaused gate it as usual.
      // Resync index so the next tick advances from the user's selection.
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            offscreenPaused = !entry.isIntersecting;
            if (offscreenPaused) stop();
            else start();
          });
        },
        { threshold: 0.1 }
      );
      io.observe(root);
    } else {
      offscreenPaused = false;
      start();
    }

    // Theme editor: clean up the timer when the section is unloaded.
    root.__wsAutoRotateStop = stop;
  }

  function initAll() {
    document.querySelectorAll('[data-ws-product-hero]').forEach(initAutoRotate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function () {
    document.querySelectorAll('[data-ws-product-hero]').forEach(function (root) {
      if (root.__wsAutoRotateStop) root.__wsAutoRotateStop();
    });
  });
})();
